import React, { useState } from "react";
import { Link } from "react-router-dom";
import FancyTitle from "@/components/FancyTitle";
import { api, AIRBNB_URL, CONTACT_EMAIL, MANOR_NAME, MANOR_LOCATION } from "@/lib/config";
import { ArrowRight } from "lucide-react";

function NewsletterSignup() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || busy) return;
    setBusy(true);
    try {
      await api.post("/newsletter", { email });
    } catch (err) {
      /* collected quietly — never trouble the visitor */
    } finally {
      setBusy(false);
      setDone(true);
    }
  };

  if (done) {
    return (
      <p className="font-serif text-ink/80 mt-4 max-w-md" data-testid="newsletter-thanks">
        Thank you — you are on the list. We will write now and then.
      </p>
    );
  }

  return (
    <form onSubmit={submit} className="mt-5 flex w-full max-w-md border-b border-ink/30" data-testid="newsletter-form">
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Your email"
        className="flex-1 bg-transparent py-2 font-serif text-ink placeholder:text-ink/40 focus:outline-none"
        data-testid="newsletter-email"
      />
      <button
        type="submit"
        disabled={busy}
        className="ml-3 text-ink/70 hover:text-burgundy transition-colors disabled:opacity-50 inline-flex items-center gap-2 font-sans text-[0.7rem] uppercase tracking-[0.22em]"
        data-testid="newsletter-submit"
      >
        Sign up <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
      </button>
    </form>
  );
}

export default function Footer() {
  return (
    <footer
      className="border-t border-ink/20 bg-parchment-cream-noise mt-16"
      data-testid="site-footer"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid grid-cols-1 md:grid-cols-4 gap-12">
        <div className="md:col-span-2">
          <FancyTitle as="div" className="press-title text-xl md:text-2xl text-ink uppercase tracking-[0.32em] md:tracking-[0.36em]">{MANOR_NAME}</FancyTitle>
          <div className="eyebrow mt-2">{MANOR_LOCATION}</div>
        </div>
        <div>
          <div className="eyebrow mb-4">Visit</div>
          <ul className="space-y-2 font-serif text-ink/80">
            <li><Link to="/enquire" className="hover:text-burgundy" data-testid="footer-enquire-link">Enquire</Link></li>
            <li><Link to="/gallery" className="hover:text-burgundy" data-testid="footer-gallery-link">Gallery</Link></li>
            <li><a href={AIRBNB_URL} target="_blank" rel="noopener noreferrer" className="hover:text-burgundy" data-testid="footer-airbnb-link">Book via Airbnb</a></li>
            <li><Link to="/diary" className="hover:text-burgundy" data-testid="footer-journal-link">Diary</Link></li>
            <li>
              <a
                href="https://www.instagram.com/t.h.e.magicians.h.o.u.s.e/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-burgundy"
                data-testid="footer-instagram-link"
              >
                Instagram
              </a>
            </li>
          </ul>
        </div>
        <div>
          <div className="eyebrow mb-4">Correspondence</div>
          <ul className="space-y-2 font-serif text-ink/80">
            <li>
              <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-burgundy" data-testid="footer-email-link">
                {CONTACT_EMAIL}
              </a>
            </li>
            <li>Opposite the church, Stoke Ferry</li>
            <li>Norfolk PE33</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-ink/20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
          <div className="eyebrow">The Journal</div>
          <p className="font-display text-2xl md:text-3xl text-ink mt-2 max-w-md leading-snug">
            Word from the house, now and then.
          </p>
          <NewsletterSignup />
        </div>
      </div>
      <div className="border-t border-ink/20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex flex-col md:flex-row justify-between gap-3 text-[0.72rem] font-sans uppercase tracking-[0.22em] text-ink/60">
          <div>© {new Date().getFullYear()} The Magicians' House</div>
        </div>
      </div>
    </footer>
  );
}
